import React, { useContext } from "react";
import { Helmet } from "react-helmet";
import {
  capitalizeFirstLetter,
  cn,
  imageBuilder,
  truncateText,
  getInviteDateLabels,
} from "../../lib/helpers";
import { Byline } from "./byline";
import { CardContext } from "./context";
import { PublishDate } from "./publish-date";
import { pageDocPath } from "../../../helpers";
import { toPlainText } from "../../lib/helpers";
import { Pills } from "./pills";
import { SiteContext } from "../global/site-context";
import { ArrowRight } from "../icon/arrow-left";
import { ImageBuilder } from "../global/image-builder";
import {
  faDesktop,
  faUsers,
  faCodePullRequest,
  faGlobe,
  faMoon,
  faCode,
  faBook,
  faArrowsSplitUpAndLeft,
  faBookOpenReader,
  faVideo,
  faSignal,
  faPodcast,
  faRobot,
  faArrowCircleUp,
  faPeopleGroup,
  faCalendarDays,
  faClock,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import {
  faThumbsUp,
  faIdCard,
  faComments,
  faNewspaper,
  faEnvelope,
} from "@fortawesome/free-regular-svg-icons";
import PortableText from "../portableText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SignalPill } from "../../templates/signal";
import { colorMap } from "../global/global-nav";
import { Logo } from "../icon/logo";
import { IntegrationsTiles } from "../playbook-page";
import { SectionContext } from "../sections/context";

import * as styles from "./card.module.css";

export const iconMap = new Map([
  ["product", { icon: faDesktop }],
  ["crm", { label: "CRM", icon: faIdCard }],
  ["gtm", { label: "CRM", icon: faIdCard }],
  ["social", { icon: faThumbsUp }],
  ["community", { icon: faUsers }],
  ["code", { label: "Open-source", icon: faCodePullRequest }],
  ["openSource", { label: "Open-source", icon: faCodePullRequest }],
  ["website", { icon: faGlobe }],
  ["forum", { icon: faComments }],
  ["news", { label: "News + events", icon: faNewspaper }],
  ["dark", { label: "Dark funnel", icon: faMoon }],
  ["custom", { icon: faCode }],
  ["email", { icon: faEnvelope }],
  ["native", { crIcon: true }],
  ["post", { label: "Blog post", icon: faNewspaper, color: "peach" }],
  ["playbook", { icon: faArrowsSplitUpAndLeft, color: "mint", size: "sm" }],
  ["page", { label: "Guide", icon: faBook, color: "melon" }],
  ["asset", { label: "Guide", icon: faBook, color: "melon" }],
  [
    "story",
    {
      label: "Customer Story",
      icon: faBookOpenReader,
      color: "robin",
    },
  ],
  ["video", { icon: faVideo, color: "gray-600" }],
  [
    "signal",
    {
      label: "Signal Guide",
      icon: faSignal,
      size: "sm",
      color: "purple-default",
    },
  ],
  [
    "scenario",
    {
      label: "Signal Scenario",
      icon: faSignal,
      size: "sm",
      color: "purple-default",
    },
  ],
  ["prompt", { icon: faRobot, size: "sm", color: "lime" }],
  ["account", { icon: faPeopleGroup, color: "mint" }],
  ["podcast", { icon: faPodcast, color: "lemon" }],
  ["starter", { icon: null, color: "peach", label: "Essential" }],
  ["team", { icon: null, color: "peach", label: "Advanced" }],
  ["starter add-on", { icon: null, color: "lemon" }],
  ["team add-on", { icon: null, color: "lemon" }],
  ["enterprise add-on", { icon: null, color: "lemon" }],
  ["enterprise", { icon: null, color: "peach" }],
  ["add-on", { icon: null }],
]);

export const PODCAST_TAG_ID = "17e46daf-7cde-44a3-b8b3-7ffcf6c7b86b";
const COMMON_ROOM_SIGNAL_PHRASE = "CR native";

const generateQuoteReviewSchema = (quote) => {
  if (!quote) return null;

  const author = quote.authors?.[0]?.author || quote.author;
  if (!author?.name) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Review",
    "reviewRating": { "@type": "Rating", "ratingValue": "5" },
    "author": { "@type": "Person", "name": author.name },
    "itemReviewed": { "@type": "Organization", "name": "Common Room" },
  };

  if (author.url) schema.author.url = author.url;
  if (author.title) schema.author.jobTitle = author.title;
  if (author.image?.asset) {
    const authorImage = imageBuilder(author.image)?.width(200)?.url();
    if (authorImage) schema.author.image = authorImage;
  }

  if (author.companies?.[0]) {
    schema.author.worksFor = { "@type": "Organization", "name": author.companies[0].title };
  }

  const reviewText = quote._rawExcerpt ? toPlainText(quote._rawExcerpt) : quote.excerpt || quote.text;
  if (reviewText) schema.reviewBody = reviewText;

  return schema;
};

export const MetaDetails = ({ card, noImage, isPodcast, isDarkBg }) => {
  const displayDate = card.showByline ? (
    <Byline />
  ) : (
    <div className={cn(
      "text-sm flex",
      isDarkBg ? "text-light-60" : "text-dark-50"
    )}
    >
      <PublishDate />
      {card.readingTimeInMinutes && (
        <>
          <span className="mx-2">&middot;</span>
          {card.readingTimeInMinutes}min read
        </>
      )}
    </div>
  );

  const summary = card.summary || toPlainText(card._rawExcerpt);
  const showSummary =
    summary &&
    (!card.quote || card.hideType && !card.showLogo) &&
    (card.showSummary || (card.allowSummary && noImage));

  return (
    <>
      {!card.hideDate && card._type === "post" && (
        <div className="mb-4">{displayDate}</div>
      )}
      {showSummary && (
        <div className={cn(
          "overflow-hidden text-sm leading-relaxed",
          isDarkBg ? "text-light-80" : null
        )}
        >
          <div className={styles.excerpt}>
            {card.fullSummary ? (
              <PortableText blocks={card._rawExcerpt} />
            ) : (
              truncateText({ str: summary, titleStr: card.title, max: 260 })
            )}
          </div>
        </div>
      )}
      <div className="mt-2">
        {card.partner && <TinyPills list={["Available via partner"]} />}
      </div>

      {!card.makeThumbnail &&
        !card.hideLabels &&
        (card.categories || card.isVideo) && (
          <div
            className={cn(
              "flex items-center flex-wrap",
              showSummary ? "mt-4" : null
            )}
          >
            <Pills
              tags={card.categories}
              bulkTags={card.tags}
              showSubTags={card.showSubTags}
            />
          </div>
        )}
    </>
  );
};

export const IMAGE_ASPECT_RATIO = 1.9;

export const EMOJI_MAP = new Map([
  ["dark-funnel", "keyhole-emoji.svg"],
  ["community-teams", "emoji-peeps.svg"],
  ["commercial-open-source-software", "emoji-avocado-vertical.svg"],
  ["product-led-growth", "emoji-glasses-face.svg"],
  ["sales", "emoji-arrow-up-right.svg"],
]);

const BACKGROUNDS_WITH_FILLED_CARDS = [
  "lightLavender",
  "gradientLavender",
  "lightGreen",
  "lavenderUltraLight",
];

export const Card = ({ className, unlinkedOverlay }) => {
  const card = useContext(CardContext);
  const section = useContext(SectionContext);
  const site = useContext(SiteContext);

  const isPodcast = card.categories?.find((c) => c._id === PODCAST_TAG_ID);

  const pastYTcutoff = card?.publishDate
    ? new Date(card.publishDate) > new Date("2023-02-06")
    : null;

  const noImage = card.hideMainMedia || card.hideImage || card.minimal;
  const image = card.graphic || card.cardImage || card.mainImage;

  const showImage = !noImage && (image || card?.imageUrl) &&
    (card.showAllImages || isPodcast || (card.imageUrl && pastYTcutoff) || (card?.graphic?.asset && card?.mainVideo?.url));

  const imageWidth = card.jumboLayout
    ? 1344
    : card.maxColumns === 1
      ? 1152
      : 836;
  const imageHeight = Math.round(imageWidth / IMAGE_ASPECT_RATIO);
  const imageSrc = showImage
    ? card?.imageUrl
      ? card?.imageUrl
      : imageBuilder(image)
        ?.width(imageWidth)
        .height(imageHeight)
        .quality(80)
        .auto("format")
        .url() || false
    : false;
  const horizontal = card.horizontalImage && site.isNotMobile;

  const logo = card.showLogo
    ? card?.companies?.[0]?.mainImage || card?.company?.mainImage
    : null;

  const skipLink =
    card.skipLink || card._type === "quotable" || (card._type === "signal" && card.status !== "published");

  const paddedCards = card._type === "story" && site.isHomepage;

  return (
    <a
      {...(skipLink ? {} : { href: card.url || pageDocPath(card) })}
      className={cn(
        "relative inline-block group rounded-xl overflow-hidden transition-card duration-200 w-full",
        card.highlighted ? "border-green-emerald border-2" : "border border-cardBorder",
        card._type === "quotable" || card.skipLink
          ? null
          : "hover:shadow-lg",
        card.isMasonry ? "mb-4 min-h-56" : "h-full",
        showImage && horizontal ? "grid grid-cols-1/3-2/3" : "flex flex-col",
        card.jumboLayout ? "grid-flow-row" : "",
        card.minimal ? "pb-1" : "",
        // card.isTall ? "h-72 md:h-96" : "",
        BACKGROUNDS_WITH_FILLED_CARDS.includes(section?.background) || BACKGROUNDS_WITH_FILLED_CARDS.includes(site?.doc.backgroundColor) && section?.background !== "black"
          ? "bg-white"
          : section.isDarkBg
            ? "bg-light-5 hover:bg-light-10"
            : "bg-dark-2",
        className
      )}
      target={card.targetBlank ? "_blank" : null}
      onClick={
        card.attribution
          ? () => {
            site.metrics.logClick(card.attribution.action);
          }
          : null
      }
    >
      {/* general card overlay */}
      {(!skipLink || !!unlinkedOverlay) && (
        <div
          className={cn(
            "absolute top-0 right-0 bottom-0 left-0 z-10 opacity-0 transition-opacity rounded-xl overflow-hidden",
            skipLink && !!unlinkedOverlay
              ? "group-hover:opacity-100 bg-white"
              : section.isDarkBg ? "group-hover:opacity-10 bg-black shadow-lg" : "group-hover:opacity-2 bg-black"
          )}
        >
          {unlinkedOverlay}
        </div>
      )}
      {/* {card.hideTitles && showImage && (
        <div className="absolute p-4">{card.title}</div>
      )} */}

      {card.makeThumbnail && !card.hideType ? (
        <div className={cn("p-4", !showImage ? "pb-0" : null)}>
          <TinyPills list={isPodcast ? ["podcast"] : [card._type]} isDarkBg={section.isDarkBg} />
        </div>
      ) : null}

      {showImage ? (
        <>
          {horizontal ? (
            <div
              className="order-2 bg-no-repeat bg-cover rounded-r-xl"
              style={{
                backgroundImage: `url('${imageSrc}')`,
                backgroundPosition: "center",
              }}
            ></div>
          ) : (
            <ImageBuilder
              src={imageSrc}
              height={imageHeight}
              width={imageWidth}
              alt={
                card?.cardImage || card?.imageUrl
                  ? card.title
                  : card.mainImage?.alt
              }
              className={cn(
                "relative w-full",
                horizontal
                  ? "rounded-r-xl"
                  : card.makeThumbnail
                    ? "rounded-none"
                    : "rounded-t-xl"
              )}
              style={{
                aspectRatio: "1.9",
                maxWidth: card.maxColumns !== 1 ? "36rem" : null,
                objectFit: "cover",
              }}
            />
          )}
        </>
      ) : null}
      <div
        className={cn(
          "text-inherit relative overflow-hidden p-4",
          paddedCards ? "p-4 md:p-7" : null,
          card.cta ? "flex-1 pb-10 md:pb-16" : null,
          card._type === "signal"
            ? "md:p-5 md:pr-4 flex-1 pb-10 md:pb-16"
            : null
        )}
        style={{
          minHeight: "auto",
        }}
      >
        {card.title && (!card.hideTitles || !showImage) && !(isPodcast && !card.graphic) && (
          <div className="break-words inline-block w-full mb-2">
            {card.gated && (
              <ImageBuilder
                src="/static/img/icons/lock.svg"
                className="float-right ml-6 w-4"
              />
            )}
            <h3
              className={cn(
                card.minimal
                  ? "text-lg font-semibold"
                  : card.jumboLayout
                    ? "text-xl md:text-2xl"
                    : noImage && !card.makeThumbnail
                      ? "text-2xl font-medium md:leading-tight"
                      : "text-lg md:text-xl font-semibold", // most common
                "tracking-tight md:leading-snug"
              )}
            >
              {card.title}
            </h3>
          </div>
        )}
        {logo && !card.makeThumbnail && (
          <ImageBuilder
            image={logo}
            quality={100}
            height={80}
            style={{
              maxHeight: "100px",
              maxWidth: "180px",
            }}
            className="-mt-4"
            alt={card.companies?.[0]?.title || card.company?.title}
          />
        )}
        <StoryDetails card={card} />
        <QuoteDetails card={card} logo={logo} isPodcast={isPodcast} />
        <MetaDetails card={card} noImage={noImage} isPodcast={isPodcast} isDarkBg={section.isDarkBg} />
        <InviteDetails card={card} />
        <div className="mb-4">
          {card._type === "invite" && !card.hideByline && <Byline />}
        </div>
        <IntegrationDetails card={card} />
        <SignalDetails card={card} />
        <CardCta
          isDarkBg={section.isDarkBg}
          className={paddedCards ? "mt-2 md:mt-0 md:ml-3 mb-1" : null}
          card={card}
        />
      </div>
    </a>
  );
};

export const CardCta = ({ card, className, isDarkBg }) =>
  card?.cta ? (
    card._type === "invite" ? (
      <div
        className={cn(
          "mt-4 inline-flex items-center gap-1.5 text-sm font-medium absolute bottom-6 left-4 px-4 py-2 rounded-full bg-black text-white",
          className
        )}
      >
        {card?.cta}
        {!card?.skipLink && <ArrowRight />}
      </div>
    ) : (
      <div
        className={cn(
          "mt-4 flex items-center text-sm font-medium absolute bottom-6 left-4",
          card?.skipLink
            ? "text-secondary"
            : isDarkBg,
          card?.hideType ? "text-textLink" : "text-auxiliary",
          className
        )}
      >
        {card?.cta}
        {!card?.skipLink && (
          <div className="ml-1.5 relative top-px">
            <ArrowRight />
          </div>
        )}
      </div>
    )
  ) : null;

const InviteDetails = ({ card }) => {
  if (card._type !== "invite") return null;

  const { dateLabel, timeLabel } = getInviteDateLabels(card);

  return (
    <ul className="flex flex-col gap-2 text-sm text-secondary mb-8">
      {card.recurring ? (
        <li>Recurring event</li>
      ) : (
        dateLabel && (
          <li className="flex items-center gap-4">
            <span className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendarDays} className="w-4 text-green-emerald opacity-80" />
              {dateLabel}
            </span>
            {timeLabel && (
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="w-4 text-green-emerald opacity-80" />
                {timeLabel}
              </span>
            )}
          </li>
        )
      )}
      {card.location && (
        <li className="flex items-center gap-2">
          <FontAwesomeIcon icon={faLocationDot} className="w-4 text-green-emerald opacity-80" />
          {card.location}
        </li>
      )}
    </ul>
  );
};

const IntegrationDetails = ({ card }) =>
  card.integrations && (card._type !== "story" || card.showIntegrations) ? (
    <div className="flex items-top mt-4 gap-2 flex-wrap">
      <IntegrationsTiles integrations={card.integrations} unlinked={true} />
    </div>
  ) : null;

const QuoteDetails = ({ card, logo, isPodcast }) => {
  const reviewSchema = card.quote ? generateQuoteReviewSchema(card.quote) : null;

  return card.isTall ? (
    card.quote ? (
      <div className={card.makeThumbnail ? "mb-4" : "mb-4"}>
        {reviewSchema && (
          <Helmet
            script={[{
              type: "application/ld+json",
              innerHTML: JSON.stringify(reviewSchema),
            }]}
          />
        )}
        <CardContext.Provider
          value={{
            authors: [
              {
                author: card.quote.authors
                  ? card.quote.authors[0].author
                  : card.quote.author,
              },
            ],
            skipAuthorLink: true,
          }}
        >
          <PortableText blocks={card.quote._rawExcerpt} className="mb-6" />
          {logo && card.makeThumbnail && (
            <ImageBuilder
              image={logo}
              quality={100}
              height={80}
              style={{
                maxHeight: "100px",
                maxWidth: "180px",
              }}
              className="-mt-4"
            />
          )}
          <Byline hideBorder={true} skipCompany={card.showLogo} />
        </CardContext.Provider>
      </div>
    ) : null
  ) : null;
};

// @todo group by source group not sourceType

const SignalDetails = ({ card }) =>
  card.sourceType && card.strength ? (
    <>
      <div className="flex flex-wrap my-2">
        <TinyPills
          list={
            card._type === "prompt"
              ? card.sourceType.filter((s) => s !== "custom")
              : card.sourceType
          }
        />
      </div>
      {card.showIntentTag && (
        <div className="absolute bottom-4 right-4 md:bottom-5 md:right-5 whitespace-nowrap">
          <SignalPill strength={card.strength} />
        </div>
      )}
    </>
  ) : null;

export const TinyPills = ({ list, isDarkBg }) => (
  <p className="leading-tight text-xs flex flex-wrap">
    {list.map((p, key) => {
      const icon = iconMap.get(p);

      return (
        <span
          key={key}
          className={cn(
            "flex items-center px-2 py-1 mr-1 mb-1 whitespace-nowrap rounded-md",
            icon?.color ? `bg-${icon.color}` : "text-purple-deep bg-dark-5",
            isDarkBg ? "text-black" : null,
          )}
          style={
            icon?.color && !isDarkBg
              ? {
                color: colorMap.get(icon.color),
              }
              : null
          }
        >
          {icon?.crIcon ? (
            <>
              <Logo iconOnly={true} className="w-4 mr-1" />
              {COMMON_ROOM_SIGNAL_PHRASE}
            </>
          ) : iconMap.has(p) ? (
            <>
              <FontAwesomeIcon
                icon={iconMap.get(p).icon}
                size={iconMap.get(p).size || "lg"}
                className="mr-1.5"
              />
              {iconMap.get(p).label || capitalizeFirstLetter(p)}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faBook} size="lg" className="mr-2 max-h-4" />
              {capitalizeFirstLetter(p)}
            </>
          )}
        </span>
      );
    })}
  </p>
);

const StoryDetails = ({ card }) =>
  card._type === "story" && card?.statistics?.[0] ? (
    <div className="rounded-full text-black font-medium px-4 py-2 mb-4 inline-block border border-green-emerald bg-dark-2 text-primary">
      <p className="flex gap-2 items-center">
        <FontAwesomeIcon icon={faArrowCircleUp} size="sm" className="max-h-4 text-green-emerald" />
        {card.statistics[0].number} {card.statistics[0].suffix}
      </p>
    </div>
  ) : null;
